import { useCallback, useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { IconArrowRight } from '@tabler/icons-react';
import { createTheme, theme as flowbiteTheme } from 'flowbite-react';

import Dialog from 'shared/Dialog';
import Button from 'shared/Button';
import { usePut } from 'utils/hooks/useAPI';
import { useAuthContext } from 'utils/contexts/AuthContext';
import { useHintContext } from 'utils/contexts/HintContext';

import { Benefits } from './Benefits';
import { Description } from './Description';

const theme = createTheme({
    modal: {
        root: {
            base:
                flowbiteTheme.modal.root.base +
                ' max-h-lg:items-start items-start lg:items-center',
        },
        content: {
            inner:
                flowbiteTheme.modal.content.inner +
                ' !max-h-min xl:max-h-[90vh]',
            base:
                flowbiteTheme.modal.content.base +
                ' BASE max-h-lg:top-10 top-10 lg:top-auto md:h-full xl:h-auto',
        },
        header: {
            base: 'border-none pt-6 px-10 mb-10',
            title: 'text-3xl font-semibold text-primary-700 text-center',
            close: {
                base: 'hidden',
            },
        },
        body: {
            base: 'px-10',
        },
        footer: {
            base: 'border-none pb-6 px-10 flex justify-center mt-10',
        },
    },
}).modal;

const FIRST_VISIT_API_URL = '/helios-api/account/first-v2-visit';

export const WelcomeDialog = () => {
    const { account } = useAuthContext();
    const { getFlag } = useHintContext();

    const { t } = useTranslation();

    const [{ loading }, putFirstVisit] = usePut<never>(FIRST_VISIT_API_URL);

    const [isDialogVisible, setIsDialogVisible] = useState(
        getFlag('v2_button', false) && !account.first_v2_visit_date
    );

    useEffect(() => {
        setIsDialogVisible(
            getFlag('v2_button', false) && !account.first_v2_visit_date
        );
    }, [getFlag, account.first_v2_visit_date]);

    const closeDialog = useCallback(() => {
        putFirstVisit();
        setIsDialogVisible(false);
    }, [putFirstVisit]);

    return (
        <Dialog
            visible={isDialogVisible}
            headerText={t('welcome_dialog:title')}
            footer={
                <Button onClick={closeDialog} loading={loading}>
                    <span className="flex gap-2 items-center">
                        <span>{t('welcome_dialog:button')}</span>
                        <IconArrowRight size={16} />
                    </span>
                </Button>
            }
            theme={theme}
            size="5xl"
        >
            <div className="flex flex-col items-center gap-2.5">
                <Benefits />
                <Description />
            </div>
        </Dialog>
    );
};
